import { createTheme } from "@mui/material/styles";

/**
 * Central MUI theme for TeamPlayer Admin.
 *
 * This is the core of the SaaS refresh: it gives every MUI component
 * (Button, Card, TextField, Table, Dialog, Menu, Chip, Tooltip) a
 * consistent, modern default so the polish cascades across all pages
 * without touching them individually.
 *
 * Values mirror the CSS custom properties in src/Assets/css/tokens.css.
 */

const colors = {
  primary: "#1273b8",
  primaryDark: "#0f5f99",
  primaryLight: "#e8f2fb",
  text: "#0f1b2d",
  textMuted: "#5b6b82",
  border: "#e6eaf0",
  surface: "#ffffff",
  bg: "#f6f8fb",
  hover: "#f1f5fa",
  tableHead: "#eef4fb",
  success: "#15a06e",
  warning: "#d9920a",
  error: "#dc4040",
};

const shadowCard =
  "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)";
const shadowElevated = "0 8px 24px rgba(16,24,40,0.10)";

// MUI requires exactly 25 shadow slots. Soften the whole scale.
const shadows = [
  "none",
  shadowCard,
  shadowCard,
  shadowCard,
  shadowCard,
  shadowCard,
  shadowCard,
  shadowCard,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
  shadowElevated,
];

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: "#ffffff",
    },
    success: { main: colors.success },
    warning: { main: colors.warning },
    error: { main: colors.error },
    info: { main: colors.primary },
    text: {
      primary: colors.text,
      secondary: colors.textMuted,
    },
    background: {
      default: colors.bg,
      paper: colors.surface,
    },
    divider: colors.border,
  },

  shape: { borderRadius: 12 },

  shadows,

  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 600, letterSpacing: "-0.02em" },
    h2: { fontWeight: 600, letterSpacing: "-0.02em" },
    h3: { fontWeight: 600, letterSpacing: "-0.01em" },
    h4: { fontWeight: 600, fontSize: "1.375rem", lineHeight: 1.3 }, // ~22px page title
    h5: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.35 }, // ~18px section header
    h6: { fontWeight: 600, fontSize: "1rem", lineHeight: 1.4 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: colors.textMuted },
    body1: { fontSize: "0.875rem", lineHeight: 1.55 }, // 14px body
    body2: { fontSize: "0.8125rem", lineHeight: 1.5, color: colors.textMuted },
    button: { textTransform: "none", fontWeight: 600 },
    caption: { fontSize: "0.75rem", color: colors.textMuted },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 18px",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        startIcon: {
          "& svg": { width: 16, height: 16 },
        },
        endIcon: {
          "& svg": { width: 16, height: 16 },
        },
        containedPrimary: {
          "&:hover": { backgroundColor: colors.primaryDark },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.text,
          "&:hover": {
            borderColor: colors.primary,
            backgroundColor: colors.primaryLight,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: 16 },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: shadowCard,
          backgroundImage: "none",
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: colors.surface,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cdd6e2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary,
            borderWidth: 1.5,
          },
        },
        input: { padding: "11px 14px" },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { color: colors.textMuted, "&.Mui-focused": { color: colors.primary } },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.border,
          fontSize: "0.875rem",
        },
        head: {
          fontWeight: 600,
          color: colors.text,
          backgroundColor: colors.tableHead,
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: shadowElevated },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          boxShadow: shadowElevated,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          backgroundColor: colors.text,
          fontSize: "0.75rem",
          padding: "6px 10px",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500 },
      },
    },
  },
});

export default theme;
